import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useUpdateImageCaption } from '../../api/queries/entities';

interface Props {
  entityId: string;
  entityImageId: string;
  caption: string | null | undefined;
}

/**
 * Inline editor for the Florence-2 caption that the GPU pipeline assigns to a
 * reference image. Saves on Enter or check-icon, cancels on Escape. Shown under
 * each thumbnail on the entity detail page.
 */
export function ImageCaptionEditor({
  entityId,
  entityImageId,
  caption,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(caption ?? '');
  const update = useUpdateImageCaption(entityId);

  useEffect(() => {
    if (!editing) setDraft(caption ?? '');
  }, [caption, editing]);

  const handleSave = async () => {
    if (draft === (caption ?? '')) {
      setEditing(false);
      return;
    }
    await update.mutateAsync({
      entityImageId,
      caption: draft.trim() ? draft.trim() : null,
    });
    setEditing(false);
  };

  if (!editing) {
    const trimmed = (caption ?? '').trim();
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="flex-start"
        sx={{ width: '100%', minHeight: 24 }}
      >
        <AutoAwesomeIcon
          sx={{
            fontSize: 14,
            color: trimmed ? 'primary.main' : 'text.disabled',
            mt: '2px',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {trimmed ? (
            <Typography
              variant="caption"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                fontFamily: 'monospace',
                fontSize: 10.5,
                lineHeight: 1.3,
                color: 'text.secondary',
              }}
              title={trimmed}
            >
              {trimmed}
            </Typography>
          ) : (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontStyle: 'italic', fontSize: 10.5 }}
            >
              No caption — generated when you train this entity's LoRA.
            </Typography>
          )}
        </Box>
        <Tooltip title="Edit caption">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
            sx={{ p: 0.25 }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
      <TextField
        autoFocus
        size="small"
        fullWidth
        multiline
        maxRows={4}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setEditing(false);
          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            void handleSave();
          }
        }}
        slotProps={{
          input: { sx: { fontSize: 11, fontFamily: 'monospace', py: 0.5 } },
        }}
        placeholder="ohwx_<entity>, ..."
        helperText="Cmd/Ctrl+Enter to save, Esc to cancel"
      />
      <Stack spacing={0.25}>
        <Tooltip title="Save">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              void handleSave();
            }}
            disabled={update.isPending}
            sx={{ p: 0.25 }}
          >
            {update.isPending ? (
              <CircularProgress size={14} />
            ) : (
              <CheckIcon sx={{ fontSize: 14 }} color="success" />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title="Cancel">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(false);
            }}
            sx={{ p: 0.25 }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
